import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { ComponentType, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { CompanyOnboardingRecordsStepKey } from '~queries/companyOnboardingRecords';
import useCompanyOnboardingRecordsQuery from '~queries/useCompanyOnboardingRecordsQuery';

import {
  OnboardingCard,
  SecondaryText,
  SettingsPageLink,
  TitleText,
} from '../CompanyOnboardingPrimitives';

export interface CreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LIST_MAX_HEIGHT_IN_PX = 320;

interface CompanyOnboardingRecordsPanelProps {
  CreateDialog?: ComponentType<CreateDialogProps>;
  companyId: string;
  detailColumnLabelKey?: string;
  settingsLink: string;
  stepKey: CompanyOnboardingRecordsStepKey;
  titleKey?: string;
}

const CompanyOnboardingRecordsPanel = ({
  CreateDialog,
  companyId,
  detailColumnLabelKey,
  settingsLink,
  stepKey,
  titleKey = `company_onboarding.modules.${stepKey}.title`,
}: CompanyOnboardingRecordsPanelProps) => {
  const { hasMore, isError, isPending, records, refetch } =
    useCompanyOnboardingRecordsQuery({ companyId, stepKey });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const hasLoadedRecords = !isPending && !isError;

  const renderRecords = () => {
    if (isPending) {
      return <LinearProgress />;
    }

    if (isError) {
      return (
        <Typography sx={{ color: 'error.main' }}>
          {t('error_messages.company_onboarding.failed_to_fetch_records')}
        </Typography>
      );
    }

    if (!records.length) {
      return (
        <SecondaryText data-test-id="company-onboarding-records-empty">
          {t('company_onboarding.records.empty')}
        </SecondaryText>
      );
    }

    return (
      <Box sx={{ maxHeight: LIST_MAX_HEIGHT_IN_PX, overflowY: 'auto' }}>
        <Table
          size="small"
          stickyHeader
          sx={(theme) => ({
            '& .MuiTableCell-root': { borderColor: theme.semantic.borderStrong },
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t('form_fields.name')}</TableCell>
              {detailColumnLabelKey ? (
                <TableCell>{t(detailColumnLabelKey)}</TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.name}</TableCell>
                {detailColumnLabelKey ? (
                  <TableCell sx={{ color: 'text.secondary' }}>{record.detail}</TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <OnboardingCard data-test-id="company-onboarding-records" sx={{ mt: 3, p: 3 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}>
        <TitleText>{t(titleKey)}</TitleText>
        {hasLoadedRecords ? (
          <Chip
            data-test-id="company-onboarding-records-count"
            label={
              hasMore
                ? t('company_onboarding.records.count_more', { count: records.length })
                : records.length
            }
            size="small"
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {CreateDialog ? (
          <SmallButton
            color="brandV2Yellow"
            data-test-id="company-onboarding-records-add"
            onClick={() => setIsCreateDialogOpen(true)}
            startIcon={<Add />}
          >
            {t(`company_onboarding.records.add.${stepKey}`)}
          </SmallButton>
        ) : null}
      </Box>

      {renderRecords()}

      {hasMore ? (
        <SettingsPageLink settingsLink={settingsLink} sx={{ fontSize: 13 }}>
          {t('company_onboarding.records.see_all', { count: records.length })}
        </SettingsPageLink>
      ) : null}

      {CreateDialog ? (
        <CreateDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSuccess={() => void refetch()}
        />
      ) : null}
    </OnboardingCard>
  );
};

export default CompanyOnboardingRecordsPanel;
